import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormSelect,
  useMultiStepForm,
  Button,
  FormItem,
  FormDatePicker,
  FormLabel,
  FormMessage,
  Input,
  FormToggleGroup,
} from "web-design-system";
import {
  useInvestorProfile,
  useCreateInvestorProfileDetails,
  useUpdateInvestorProfileDetails,
  useCountryOptions,
} from "creso-core/hooks";
import { PersonalInfoSchema } from "creso-core/zod";
import { DEFAULT_DOB_DATE, GENDER } from "creso-core/constants";
import { Client } from "creso-core/types";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { AutofilledData } from "./autofilled-data";

export function PersonalInfo() {
  const { client } = useClientOnboarding();
  const { nextStep } = useMultiStepForm();

  const { data: profile } = useInvestorProfile(
    { investment_account_guid: client?.investment_account ?? "" },
    {
      enabled: true,
    }
  );

  const { mutateAsync: createProfile } = useCreateInvestorProfileDetails();

  const { mutateAsync: updateProfile } = useUpdateInvestorProfileDetails();

  const countryOptions = useCountryOptions();

  const form = useForm<z.infer<typeof PersonalInfoSchema>>({
    resolver: zodResolver(PersonalInfoSchema),
    values: {
      name: profile?.name || client?.name || "",
      pan: profile?.pan ?? client?.pan ?? "",
      gender: profile?.gender ?? "",
      date_of_birth: profile?.date_of_birth ?? DEFAULT_DOB_DATE,
      country_of_birth:
        countryOptions?.find((c) => c.value === profile?.country_of_birth)
          ?.value ?? "IN",
    },
  });

  const { isDirty } = form.formState;

  const onSubmit = async (data: z.infer<typeof PersonalInfoSchema>) => {
    try {
      if (profile) {
        await updateProfile({
          ...data,
          investment_account_guid: client?.investment_account ?? "",
          autofilled: profile?.autofilled ? !isDirty : false,
        });
      } else {
        await createProfile({
          ...data,
          investment_account_guid: client?.investment_account ?? "",
        });
      }
      nextStep();
    } catch (error) {
      handleError(error, "Failed to update profile");
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Personal information</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="pan"
            render={({ field }) => (
              <FormItem>
                <FormLabel>PAN</FormLabel>
                <div className="flex flex-row items-start gap-2">
                  <div className="flex flex-col flex-1 gap-1.5">
                    <Input
                      {...field}
                      disabled
                      onChange={(e) =>
                        field.onChange(
                          e.target.value?.toLocaleUpperCase() || ""
                        )
                      }
                      placeholder="ABCDE1234J"
                    />
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormInput
                {...field}
                label="Name"
                placeholder="Enter name"
                description="As mentioned in PAN card"
                disabled
              />
            )}
          />
          <FormField
            control={form.control}
            name="gender"
            className="w-full"
            render={({ field }) => (
              <FormToggleGroup
                options={GENDER.map((gender) => ({
                  label: gender.label,
                  value: gender.value,
                }))}
                label="Gender"
                {...field}
              />
            )}
          />
          <div className="flex w-full items-start justify-between gap-4">
            <FormField
              control={form.control}
              className="flex-1"
              name="date_of_birth"
              render={({ field }) => (
                <FormDatePicker
                  label="Date of birth"
                  format="yyyy-MM-dd"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date > today;
                  }}
                />
              )}
            />
            <FormField
              className="flex-1"
              control={form.control}
              name="country_of_birth"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Country of birth"
                  placeholder="Select country"
                  options={countryOptions}
                />
              )}
            />
          </div>
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <AutofilledData
              client={client as Client}
              autofillSource={!isDirty ? profile?.autofill_source : null}
            />
            <div className="flex items-center w-full gap-5">
              <Button
                disabled={form.formState.isSubmitting}
                className="w-full"
                type="submit"
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
